import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useCallback,
} from "react";
import {
  Select,
  MenuItem,
  FormControl,
  Checkbox,
  ListItemText,
  SelectChangeEvent,
} from "@mui/material";

interface Option {
  value: string;
  label: string;
}

interface CustomSelectProps {
  options: Option[];
  isMulti?: boolean; // If true, allows multiple selection
  label: string;
  placeholder?: string;
  onChange: (selected: string | string[]) => void;
  className?: string;
  onBlurred?: () => void;
  disabled?: boolean;
  autoSelect?: boolean;
  error?: boolean; // Add error prop for validation
}

// Define the type of the methods that will be available on the ref
export interface CustomSelectRef {
  reset: () => void;
  setSeletedOption: (value: string | string[]) => void;
}

const CustomSelect = forwardRef<CustomSelectRef, CustomSelectProps>(
  (
    {
      options,
      isMulti = false,
      placeholder = "Select an option",
      onChange,
      className,
      onBlurred,
      disabled,
      autoSelect = false,
      error = false,
    }: CustomSelectProps,
    ref
  ) => {
    const [selectedValue, setSelectedValue] = useState<string | string[]>(
      isMulti ? [] : ""
    );

    const handleAutoSelect = useCallback(() => {
      if (autoSelect && options?.length > 0 && !disabled && !selectedValue) {
        const initialValue = isMulti ? [options[0].value] : options[0].value;
        setSelectedValue(initialValue);
        onChange(initialValue);
      }
    }, [autoSelect, options, isMulti, disabled, onChange, selectedValue]);

    useEffect(() => {
      handleAutoSelect();
    }, [handleAutoSelect]);

    useImperativeHandle(ref, () => ({
      reset() {
        setSelectedValue(isMulti ? [] : "");
      },
      setSeletedOption(value: string | string[]) {
        setSelectedValue(
          value
            ? isMulti
              ? Array.isArray(value)
                ? value
                : [value]
              : value
            : isMulti
            ? []
            : ""
        );
      },
    }));

    const handleChange = (event: SelectChangeEvent<string | string[]>) => {
      const { value } = event.target;
      const newValue = isMulti && Array.isArray(value) ? value : value;
      setSelectedValue(newValue);
      onChange(newValue);
    };

    const handleOnBlur = () => {
      if (onBlurred) onBlurred();
    };

    // Determine border color based on error state
    const getBorderColor = () => {
      if (error) return "#ff0000"; // Error color
      if (disabled) return "#E5E7EB"; // Disabled color
      return "#CBD2D9"; // Default color
    };

    // Determine border width based on state
    const getBorderWidth = () => {
      if (error) return "1px"; // Error state
      return "1px"; // Default state
    };

    return (
      <FormControl
        error={error}
        sx={{
          width: "100%",
          "& .MuiOutlinedInput-root": {
            background: "#fff",
            fontSize: "16px",
            fontWeight: 500,
            borderRadius: "3px",
            width: "100%",
            border: `${getBorderWidth()} solid ${getBorderColor()}`,
            transition: "all 0.2s ease",
            minHeight: "40px",
            height: "40px",
            boxSizing: "border-box",
            "&:hover": {
              borderColor: error ? "#ff0000" : "#19B5FE",
              borderWidth: error ? "1px" : "1.5px",
            },
            "&.Mui-focused": {
              borderColor: error ? "#ff0000" : "#19B5FE",
              borderWidth: error ? "1px" : "1.5px",
              boxShadow: error
                ? "0 0 0 2px rgba(255, 0, 0, 0.12)"
                : "0 0 0 2px rgba(25, 181, 254, 0.12)",
            },
            "&.Mui-disabled": {
              backgroundColor: "#F5F7F7",
              borderColor: "#E5E7EB",
            },
            "& fieldset": {
              border: "none",
            },
          },
          "& .MuiSelect-select": {
            padding: "10px 14px",
            fontSize: "15px",
            fontWeight: 500,
            color: "#59585B",
            minHeight: "40px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            boxSizing: "border-box",
            "&.Mui-disabled": {
              color: "#838285",
            },
            "&.MuiSelect-displayEmpty": {
              color: "#cbd2d9", // Placeholder color matching input styling
            },
          },
          "& .MuiSelect-icon": {
            color: error ? "#ff0000" : "#59585B",
            right: "12px",
          },
        }}
      >
        <Select
          disabled={disabled}
          onBlur={handleOnBlur}
          multiple={isMulti}
          value={selectedValue}
          onChange={handleChange}
          className={className}
          displayEmpty
          renderValue={(selected) => {
            if (
              !selected ||
              (Array.isArray(selected) && selected.length === 0)
            ) {
              return (
                <span style={{ color: "#cbd2d9", fontWeight: 500 }}>
                  {placeholder}
                </span>
              );
            }
            if (isMulti && Array.isArray(selected)) {
              return selected
                .map(
                  (value) =>
                    options.find((option) => option.value === value)?.label ||
                    ""
                )
                .join(", ");
            }
            const selectedOption = options.find(
              (option) => option.value === selected
            );
            return selectedOption ? selectedOption.label : placeholder;
          }}
          MenuProps={{
            PaperProps: {
              style: {
                borderRadius: "8px",
                border: "1px solid #CBD2D9",
                boxShadow: "0 8px 32px 0 rgba(25, 181, 254, 0.10)",
                marginTop: "4px",
                maxHeight: 320,
                overflowY: "auto",
                padding: 0,
              },
            },
          }}
        >
          {options?.map((option, index) => (
            <MenuItem
              key={index}
              value={option.value}
              className="text-mainText font-bold text-[14px] border-b border-[#F3F4F6] last:border-b-0 hover:bg-[#F3F4F6]"
              sx={{
                padding: "12px 18px",
                fontSize: "15px",
                fontWeight: 500,
                color: "#59585B",
                minHeight: "44px",
                maxHeight: "44px",
                borderBottom: "1px solid #F3F4F6",
                "&:last-child": {
                  borderBottom: "none",
                },
                "&:hover": {
                  backgroundColor: "#F3F4F6",
                },
                "&.Mui-selected": {
                  backgroundColor: "#E3F2FD",
                  color: "#1976D2",
                  "&:hover": {
                    backgroundColor: "#E3F2FD",
                  },
                },
              }}
            >
              {isMulti ? (
                <>
                  <Checkbox
                    checked={
                      Array.isArray(selectedValue) &&
                      selectedValue.includes(option.value)
                    }
                    className="text-blueText"
                  />
                  <ListItemText primary={option.label} />
                </>
              ) : (
                option.label
              )}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }
);

CustomSelect.displayName = "CustomSelect";

export default CustomSelect;
